import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import { useUser } from '../UserContext'
import { withOrg } from '../lib/withOrg'

const EMPTY_CONTACT = {
  first_name: '',
  last_name: '',
  job_title: '',
  email: '',
  phone: '',
  notes: ''
}

const EMPTY_CLIENT_FORM = {
  name: '',
  industry: '',
  primary_contact_name: '',
  primary_contact_email: '',
  notes: ''
}

export default function ClientDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const { orgId, can } = useUser()
  const canViewFinancials = can('financials.view')

  const [client, setClient] = useState(null)
  const [contacts, setContacts] = useState([])
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [showContactForm, setShowContactForm] = useState(false)
  const [showEditClient, setShowEditClient] = useState(false)
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT)
  const [clientForm, setClientForm] = useState(EMPTY_CLIENT_FORM)
  const [uploadingLogo, setUploadingLogo] = useState(false)

  useEffect(() => {
    fetchClient()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function fetchClient() {
    setLoading(true)
    setError(null)

    const [
      { data: clientData, error: clientError },
      { data: contactData, error: contactError },
      { data: dealData, error: dealError }
    ] = await Promise.all([
      supabase
        .from('clients')
        .select('*')
        .eq('id', id)
        .maybeSingle(),

      supabase
        .from('contacts')
        .select('*')
        .eq('client_id', id)
        .order('created_at', { ascending: false }),

      supabase
        .from('secure_deals')
        .select('id, name, value, currency, status')
        .eq('client_id', id)
        .order('created_at', { ascending: false })
    ])

    if (clientError || !clientData) {
      setError(clientError?.message || 'Client not found.')
      setLoading(false)
      return
    }

    if (contactError) console.error('Failed to load contacts:', contactError)
    if (dealError) console.error('Failed to load deals:', dealError)

    setClient(clientData)
    setClientForm({
      name: clientData.name || '',
      industry: clientData.industry || '',
      primary_contact_name: clientData.primary_contact_name || '',
      primary_contact_email: clientData.primary_contact_email || '',
      notes: clientData.notes || ''
    })
    setContacts(contactData || [])
    setDeals(dealData || [])
    setLoading(false)
  }

  async function saveClient() {
    if (!clientForm.name.trim()) {
      toast.error('Client name is required')
      return
    }

    setSaving(true)

    const { error } = await supabase
      .from('clients')
      .update({
        name: clientForm.name.trim(),
        industry: clientForm.industry.trim() || null,
        primary_contact_name: clientForm.primary_contact_name.trim() || null,
        primary_contact_email: clientForm.primary_contact_email.trim() || null,
        notes: clientForm.notes.trim() || null
      })
      .eq('id', id)

    setSaving(false)

    if (error) {
      toast.error(`Failed to update client: ${error.message}`)
      return
    }

    toast.success('Client updated')
    setShowEditClient(false)
    fetchClient()
  }

  async function uploadLogo(file) {
  if (!file) return

  try {
    setUploadingLogo(true)

    const ext = file.name.split('.').pop()
    const fileName = `${client.id}-${Date.now()}.${ext}`

    const { error: uploadError } = await supabase.storage
      .from('client-logo')
      .upload(fileName, file, {
        upsert: true
      })

    if (uploadError) throw uploadError

    const { data } = supabase.storage
      .from('client-logo')
      .getPublicUrl(fileName)

    const logoUrl = data.publicUrl

    const { error: updateError } = await supabase
      .from('clients')
      .update({
        logo_url: logoUrl
      })
      .eq('id', client.id)

    if (updateError) throw updateError

    toast.success('Logo uploaded')

    fetchClient()
  } catch (err) {
    console.error(err)
    toast.error(err.message)
  } finally {
    setUploadingLogo(false)
  }
}

  async function addContact() {
    if (!contactForm.first_name.trim()) {
      toast.error('First name is required')
      return
    }

    setSaving(true)

    const payload = withOrg({
      client_id: id,
      first_name: contactForm.first_name.trim(),
      last_name: contactForm.last_name.trim() || null,
      job_title: contactForm.job_title.trim() || null,
      email: contactForm.email.trim() || null,
      phone: contactForm.phone.trim() || null,
      notes: contactForm.notes.trim() || null
    }, orgId)

    const { error } = await supabase
      .from('contacts')
      .insert([payload])

    setSaving(false)

    if (error) {
      toast.error(`Failed to add contact: ${error.message}`)
      return
    }

    toast.success('Contact added')
    setContactForm(EMPTY_CONTACT)
    setShowContactForm(false)
    fetchClient()
  }

  function currencySymbol(c) {
    return c === 'GBP' ? '£' : c === 'EUR' ? '€' : '$'
  }

  if (loading) {
    return (
      <div className="hv-page" style={{ color: 'var(--hv-text-muted)', paddingTop: '60px' }}>
        Loading...
      </div>
    )
  }

  if (error || !client) {
    return (
      <div className="hv-page">
        <button className="hv-btn hv-btn-ghost" onClick={() => navigate('/clients')} style={{ marginBottom: '20px' }}>
          ← Back to Clients
        </button>

        <div style={{
          background: 'var(--hv-red-bg)',
          border: '1px solid #fca5a5',
          borderRadius: 'var(--hv-radius)',
          padding: '14px 18px',
          color: 'var(--hv-red-text)',
          fontSize: '13.5px'
        }}>
          {error || 'Client not found.'}
        </div>
      </div>
    )
  }

  return (
    <div className="hv-page">
      <div style={{ marginBottom: '20px' }}>
        <button className="hv-btn hv-btn-ghost" onClick={() => navigate('/clients')}>
          ← Back to Clients
        </button>
      </div>

      <div className="hv-page-header">
        <div>
          <h1 className="hv-page-title">{client.name}</h1>
          <p className="hv-page-subtitle">{client.industry || 'No industry set'}</p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="hv-btn hv-btn-secondary"
            onClick={() => {
              setShowEditClient(!showEditClient)
              setShowContactForm(false)
            }}
          >
            {showEditClient ? 'Cancel' : 'Edit Client'}
          </button>

          <button
            className="hv-btn hv-btn-primary"
            onClick={() => {
              setShowContactForm(!showContactForm)
              setShowEditClient(false)
            }}
          >
            {showContactForm ? 'Cancel' : 'Add Contact'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '24px' }}>
        <div className="hv-card" style={{ padding: '24px' }}>

          <div
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    marginBottom: '24px'
  }}
>
  <div
    style={{
      width: '90px',
      height: '90px',
      borderRadius: '16px',
      border: '1px solid var(--hv-border)',
      background: '#fff',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    {client.logo_url ? (
      <img
        src={client.logo_url}
        alt={client.name}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain'
        }}
      />
    ) : (
      <span
        style={{
          fontSize: '26px',
          fontWeight: '700',
          color: 'var(--hv-accent)'
        }}
      >
        {client.name?.substring(0, 2).toUpperCase()}
      </span>
    )}
  </div>

  <div>
    <label className="hv-btn hv-btn-secondary" style={{ cursor: 'pointer' }}>
      {uploadingLogo ? 'Uploading...' : 'Upload Logo'}

      <input
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => uploadLogo(e.target.files?.[0])}
      />
    </label>
  </div>
</div>

          <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px' }}>
            Client Homepage
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            <Info label="POC" value={client.primary_contact_name || '—'} />
            <Info label="Contact Email" value={client.primary_contact_email || '—'} />
            <Info label="Industry" value={client.industry || '—'} />
          </div>

          <div style={{ marginTop: '18px', paddingTop: '16px', borderTop: '1px solid var(--hv-border)' }}>
            <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', marginBottom: '6px' }}>Client Notes</div>
            <div style={{ fontSize: '14px', color: 'var(--hv-text-primary)', whiteSpace: 'pre-wrap' }}>
              {client.notes || 'No notes yet.'}
            </div>
          </div>
        </div>

        <div className="hv-card" style={{ padding: '24px' }}>
          <div style={{ fontSize: '13px', color: 'var(--hv-text-muted)', marginBottom: '6px' }}>
            Key Contacts
          </div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--hv-text-primary)' }}>
            {contacts.length}
          </div>

          <div style={{ fontSize: '13px', color: 'var(--hv-text-muted)', marginTop: '18px', marginBottom: '6px' }}>
            Deals
          </div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--hv-text-primary)' }}>
            {deals.length}
          </div>
        </div>
      </div>

      {showEditClient && (
        <div className="hv-card" style={{ padding: '24px', marginBottom: '24px' }}>
          <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '18px' }}>
            Edit Client
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px', marginBottom: '16px' }}>
            <div>
              <label className="hv-form-label">Client Name *</label>
              <input
                className="hv-input"
                value={clientForm.name}
                onChange={e => setClientForm({ ...clientForm, name: e.target.value })}
              />
            </div>

            <div>
              <label className="hv-form-label">Industry</label>
              <input
                className="hv-input"
                value={clientForm.industry}
                onChange={e => setClientForm({ ...clientForm, industry: e.target.value })}
              />
            </div>

            <div>
              <label className="hv-form-label">Primary Contact / POC</label>
              <input
                className="hv-input"
                value={clientForm.primary_contact_name}
                onChange={e => setClientForm({ ...clientForm, primary_contact_name: e.target.value })}
              />
            </div>

            <div>
              <label className="hv-form-label">Primary Contact Email</label>
              <input
                className="hv-input"
                type="email"
                value={clientForm.primary_contact_email}
                onChange={e => setClientForm({ ...clientForm, primary_contact_email: e.target.value })}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label className="hv-form-label">Client Notes</label>
              <textarea
                className="hv-input"
                rows={5}
                value={clientForm.notes}
                onChange={e => setClientForm({ ...clientForm, notes: e.target.value })}
              />
            </div>
          </div>

          <button className="hv-btn hv-btn-primary" onClick={saveClient} disabled={saving}>
            {saving ? 'Saving...' : 'Save Client'}
          </button>
        </div>
      )}

      {showContactForm && (
        <div className="hv-card" style={{ padding: '24px', marginBottom: '24px' }}>
          <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '18px' }}>
            Add Key Contact
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '16px' }}>
            <div>
              <label className="hv-form-label">First Name *</label>
              <input className="hv-input" value={contactForm.first_name} onChange={e => setContactForm({ ...contactForm, first_name: e.target.value })} />
            </div>

            <div>
              <label className="hv-form-label">Last Name</label>
              <input className="hv-input" value={contactForm.last_name} onChange={e => setContactForm({ ...contactForm, last_name: e.target.value })} />
            </div>

            <div>
              <label className="hv-form-label">Role / Job Title</label>
              <input className="hv-input" value={contactForm.job_title} onChange={e => setContactForm({ ...contactForm, job_title: e.target.value })} />
            </div>

            <div>
              <label className="hv-form-label">Email</label>
              <input className="hv-input" type="email" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} />
            </div>

            <div>
              <label className="hv-form-label">Phone</label>
              <input className="hv-input" value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} />
            </div>

            <div>
              <label className="hv-form-label">Notes</label>
              <input className="hv-input" value={contactForm.notes} onChange={e => setContactForm({ ...contactForm, notes: e.target.value })} />
            </div>
          </div>

          <button className="hv-btn hv-btn-primary" onClick={addContact} disabled={saving}>
            {saving ? 'Saving...' : 'Save Contact'}
          </button>
        </div>
      )}

      <div className="hv-table-wrap" style={{ marginBottom: '24px' }}>
        <div style={{
          padding: '14px 22px',
          borderBottom: '1px solid var(--hv-border)',
          fontSize: '13px',
          fontWeight: '600',
          background: '#FAFBFD'
        }}>
          Key Contacts
        </div>

        {contacts.length === 0 ? (
          <div className="hv-empty">
            <div className="hv-empty-title">No key contacts yet</div>
            <div className="hv-empty-text">Add client stakeholders and their roles here.</div>
          </div>
        ) : (
          <table className="hv-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Notes</th>
              </tr>
            </thead>

            <tbody>
              {contacts.map(contact => (
                <tr key={contact.id}>
                  <td className="hv-table-name">
                    {[contact.first_name, contact.last_name].filter(Boolean).join(' ')}
                  </td>
                  <td style={{ color: 'var(--hv-text-secondary)' }}>{contact.job_title || '—'}</td>
                  <td style={{ color: 'var(--hv-text-secondary)' }}>{contact.email || '—'}</td>
                  <td style={{ color: 'var(--hv-text-secondary)' }}>{contact.phone || '—'}</td>
                  <td style={{ color: 'var(--hv-text-secondary)' }}>{contact.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="hv-table-wrap">
        <div style={{
          padding: '14px 22px',
          borderBottom: '1px solid var(--hv-border)',
          fontSize: '13px',
          fontWeight: '600',
          background: '#FAFBFD'
        }}>
          Deals
        </div>

        {deals.length === 0 ? (
          <div className="hv-empty">
            <div className="hv-empty-title">No deals yet</div>
            <div className="hv-empty-text">Deals linked to this client will appear here.</div>
          </div>
        ) : (
          <table className="hv-table">
            <thead>
              <tr>
                <th>Deal</th>
                <th>Status</th>
                {canViewFinancials && <th>Value</th>}
              </tr>
            </thead>

            <tbody>
              {deals.map(deal => (
                <tr key={deal.id}>
                  <td>
                    <button
                      type="button"
                      className="hv-table-name"
                      onClick={() => navigate(`/deals/${deal.id}`)}
                      style={{
                        border: 'none',
                        background: 'none',
                        padding: 0,
                        color: 'var(--hv-accent)',
                        cursor: 'pointer',
                        fontFamily: 'var(--hv-font)'
                      }}
                    >
                      {deal.name}
                    </button>
                  </td>
                  <td style={{ color: 'var(--hv-text-secondary)' }}>{deal.status || '—'}</td>
                  {canViewFinancials && <td style={{ color: 'var(--hv-text-primary)', fontWeight: '500' }}>
                    {currencySymbol(deal.currency)}{Number(deal.value || 0).toLocaleString()}
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', marginBottom: '4px' }}>{label}</div>
      <div style={{
        fontSize: '14px',
        color: 'var(--hv-text-primary)',
        fontWeight: '500',
        wordBreak: 'break-word'
      }}>
        {value}
      </div>
    </div>
  )
}
